/**
 * CapstoneDesigner — Coach-facing designer tool for authoring capstone scenes.
 *
 * Provides:
 *   - Markdown editor for brief_md
 *   - Deliverable type builder (file/url/text rows with config)
 *   - Due date picker
 *   - Max submissions input
 *   - Resource links editor
 *   - Rubric builder integration
 *   - Live preview panel
 */
import React, { useState, useCallback } from 'react';
import RubricBuilder from '../../components/rubrics/RubricBuilder';
import { DesignerTool } from '../DesignerTool';
import { useDesigner } from '../DesignerContext';
import { requiresSceneType } from '../predicates';

const DELIVERABLE_TYPES = [
  { value: 'file', label: 'File Upload' },
  { value: 'url', label: 'URL / Link' },
  { value: 'text', label: 'Text Response' },
];

function CapstoneDesignerComponent({ sceneId, domain, metadata, onUpdate }) {
  const meta = metadata || {};
  const [briefMd, setBriefMd] = useState(meta.brief_md || '');
  const [deliverableTypes, setDeliverableTypes] = useState(meta.deliverable_types || []);
  const [dueDate, setDueDate] = useState(meta.due_date || '');
  const [maxSubmissions, setMaxSubmissions] = useState(meta.max_submissions || '');
  const [resourceLinks, setResourceLinks] = useState(meta.resource_links || []);
  const [showPreview, setShowPreview] = useState(false);
  const [showRubric, setShowRubric] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const notify = useCallback((patch) => {
    if (onUpdate) onUpdate(patch);
  }, [onUpdate]);

  const handleBriefChange = (val) => {
    setBriefMd(val);
    notify({ brief_md: val });
  };

  // ── Deliverable type management ─────────────────────────────────────

  const addDeliverable = () => {
    const next = [...deliverableTypes, { type: 'file', label: '', max_size_mb: '', allowed_extensions: [] }];
    setDeliverableTypes(next);
    notify({ deliverable_types: next });
  };

  const removeDeliverable = (idx) => {
    const next = deliverableTypes.filter((_, i) => i !== idx);
    setDeliverableTypes(next);
    notify({ deliverable_types: next });
  };

  const updateDeliverable = (idx, field, value) => {
    const next = deliverableTypes.map((d, i) => (i === idx ? { ...d, [field]: value } : d));
    setDeliverableTypes(next);
    notify({ deliverable_types: next });
  };

  // ── Resource links management ───────────────────────────────────────

  const addResourceLink = () => {
    const next = [...resourceLinks, ''];
    setResourceLinks(next);
    notify({ resource_links: next });
  };

  const removeResourceLink = (idx) => {
    const next = resourceLinks.filter((_, i) => i !== idx);
    setResourceLinks(next);
    notify({ resource_links: next });
  };

  const updateResourceLink = (idx, value) => {
    const next = resourceLinks.map((r, i) => (i === idx ? value : r));
    setResourceLinks(next);
    notify({ resource_links: next });
  };

  // ── Save all metadata ──────────────────────────────────────────────

  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (!briefMd.trim()) { setError('Capstone brief is required.'); return; }
    if (deliverableTypes.length === 0) { setError('Add at least one deliverable type.'); return; }

    setSaving(true);
    const token = localStorage.getItem('scenario_os_token');
    const payload = {
      brief_md: briefMd,
      deliverable_types: deliverableTypes,
      due_date: dueDate || null,
      max_submissions: maxSubmissions ? parseInt(maxSubmissions, 10) : null,
      resource_links: resourceLinks.filter((r) => r.trim()),
    };

    try {
      const res = await fetch(
        `/scenario-os/coach/scenes/${encodeURIComponent(sceneId)}/metadata`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      setSuccess('Capstone settings saved.');
      notify(payload);
    } catch (e) {
      setError(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  // ── Preview markdown ────────────────────────────────────────────────

  const renderMarkdown = (md) => {
    if (!md) return '<p style="color:#94a3b8">No brief content yet.</p>';
    return md
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/^### (.+)$/gm, '<h3 style="margin:10px 0 4px;font-size:14px;font-weight:700">$1</h3>')
      .replace(/^## (.+)$/gm, '<h2 style="margin:12px 0 6px;font-size:16px;font-weight:700">$1</h2>')
      .replace(/^# (.+)$/gm, '<h1 style="margin:14px 0 8px;font-size:18px;font-weight:700">$1</h1>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code style="background:#f1f5f9;padding:1px 4px;border-radius:3px;font-size:0.9em">$1</code>')
      .replace(/^- (.+)$/gm, '<li style="margin-left:16px;margin-bottom:2px">$1</li>')
      .replace(/\n\n/g, '<br/><br/>')
      .replace(/\n/g, '<br/>');
  };

  if (showRubric) {
    return (
      <div style={containerStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: '#1f2937' }}>Capstone Rubric</h2>
          <button onClick={() => setShowRubric(false)} style={secondaryBtnStyle}>
            ← Back to Designer
          </button>
        </div>
        <RubricBuilder
          sceneId={sceneId}
          domain={domain}
          existingRubric={meta.rubric}
          onSaved={() => setShowRubric(false)}
        />
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: '#1f2937', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>🎓</span> Capstone Designer
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowPreview((v) => !v)} style={secondaryBtnStyle}>
            {showPreview ? '← Edit' : 'Preview →'}
          </button>
          <button onClick={() => setShowRubric(true)} style={secondaryBtnStyle}>
            Rubric Builder
          </button>
        </div>
      </div>

      {showPreview ? (
        /* ── Preview Panel ──────────────────────────────────────── */
        <div style={previewCardStyle}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
            Preview
          </div>
          <div
            style={{ fontSize: 14, lineHeight: 1.7, color: '#1f2937' }}
            dangerouslySetInnerHTML={{ __html: renderMarkdown(briefMd) }}
          />
          {deliverableTypes.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: '#6b7280', marginBottom: 6 }}>DELIVERABLES</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {deliverableTypes.map((d, i) => (
                  <span key={i} style={{
                    background: '#eff6ff', color: '#1e40af', fontSize: 12, fontWeight: 600,
                    padding: '4px 10px', borderRadius: 999,
                  }}>
                    {d.type === 'file' ? '📎' : d.type === 'url' ? '🔗' : '📝'}{' '}
                    {d.label || d.type}
                  </span>
                ))}
              </div>
            </div>
          )}
          {dueDate && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
              Due: {dueDate}
            </div>
          )}
          {maxSubmissions && (
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              Max submissions: {maxSubmissions}
            </div>
          )}
        </div>
      ) : (
        /* ── Edit Panel ─────────────────────────────────────────── */
        <>
          {/* Brief Markdown Editor */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Capstone Brief (Markdown)</label>
            <textarea
              value={briefMd}
              onChange={(e) => handleBriefChange(e.target.value)}
              placeholder="# Capstone Project&#10;&#10;Describe the project requirements, learning objectives, and expected deliverables..."
              rows={12}
              style={textareaStyle}
            />
          </div>

          {/* Deliverable Types Builder */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Deliverable Types</label>
            {deliverableTypes.map((dt, idx) => (
              <div key={idx} style={deliverableRowStyle}>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 80px 1fr 28px', gap: 8, alignItems: 'center' }}>
                  <select
                    value={dt.type}
                    onChange={(e) => updateDeliverable(idx, 'type', e.target.value)}
                    style={inputStyle}
                  >
                    {DELIVERABLE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <input
                    value={dt.label || ''}
                    onChange={(e) => updateDeliverable(idx, 'label', e.target.value)}
                    placeholder="Label (e.g., Project Report)"
                    style={inputStyle}
                  />
                  <input
                    type="number"
                    value={dt.max_size_mb || ''}
                    onChange={(e) => updateDeliverable(idx, 'max_size_mb', e.target.value ? Number(e.target.value) : '')}
                    placeholder="MB"
                    style={inputStyle}
                    min="0"
                  />
                  <input
                    value={(dt.allowed_extensions || []).join(', ')}
                    onChange={(e) => updateDeliverable(idx, 'allowed_extensions', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                    placeholder=".pdf, .docx, .zip"
                    style={inputStyle}
                  />
                  <button onClick={() => removeDeliverable(idx)} style={iconBtnStyle} title="Remove">
                    ✕
                  </button>
                </div>
              </div>
            ))}
            <button onClick={addDeliverable} style={addRowBtnStyle}>
              + Add Deliverable Type
            </button>
          </div>

          {/* Due Date + Max Submissions */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => {
                  setDueDate(e.target.value);
                  notify({ due_date: e.target.value || null });
                }}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Max Submissions</label>
              <input
                type="number"
                value={maxSubmissions}
                onChange={(e) => {
                  setMaxSubmissions(e.target.value);
                  notify({ max_submissions: e.target.value ? parseInt(e.target.value, 10) : null });
                }}
                placeholder="Unlimited"
                min="1"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Resource Links */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Resource Links</label>
            {resourceLinks.map((link, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                <input
                  value={link}
                  onChange={(e) => updateResourceLink(idx, e.target.value)}
                  placeholder="https://..."
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button onClick={() => removeResourceLink(idx)} style={iconBtnStyle} title="Remove">
                  ✕
                </button>
              </div>
            ))}
            <button onClick={addResourceLink} style={addRowBtnStyle}>
              + Add Resource Link
            </button>
          </div>

          {/* Save + Rubric */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button onClick={handleSave} disabled={saving} style={primaryBtnStyle}>
              {saving ? 'Saving...' : 'Save Capstone'}
            </button>
            <button onClick={() => setShowRubric(true)} style={secondaryBtnStyle}>
              Edit Rubric
            </button>
          </div>
        </>
      )}

      {error && <div style={errorBannerStyle}>{error}</div>}
      {success && <div style={successBannerStyle}>{success}</div>}
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────

const containerStyle = {
  fontFamily: 'system-ui, -apple-system, sans-serif',
  maxWidth: 860,
  margin: '0 auto',
  padding: 20,
};

const labelStyle = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 4,
};

const inputStyle = {
  width: '100%',
  padding: '7px 10px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const textareaStyle = {
  width: '100%',
  padding: 12,
  border: '1px solid #d1d5db',
  borderRadius: 8,
  fontSize: 13,
  fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
  lineHeight: 1.6,
  resize: 'vertical',
  boxSizing: 'border-box',
  background: '#fafbfc',
};

const deliverableRowStyle = {
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 10,
  marginBottom: 6,
};

const iconBtnStyle = {
  background: 'none',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 13,
  padding: '2px 6px',
  color: '#dc2626',
};

const addRowBtnStyle = {
  background: '#eff6ff',
  color: '#2563eb',
  border: '1px dashed #93c5fd',
  borderRadius: 6,
  padding: '8px 14px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  width: '100%',
};

const primaryBtnStyle = {
  background: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '9px 20px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryBtnStyle = {
  background: '#fff',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  padding: '7px 14px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const previewCardStyle = {
  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  padding: 24,
};

const errorBannerStyle = {
  marginTop: 12,
  padding: '8px 12px',
  background: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: 6,
  color: '#991b1b',
  fontSize: 13,
};

const successBannerStyle = {
  marginTop: 12,
  padding: '8px 12px',
  background: '#f0fdf4',
  border: '1px solid #bbf7d0',
  borderRadius: 6,
  color: '#166534',
  fontSize: 13,
};

function CapstoneDesignerToolMount() {
  const { scene, setDomainMetadata } = useDesigner();
  if (!scene) return null;
  return (
    <CapstoneDesignerComponent
      sceneId={scene.scene_id}
      domain={scene.domain_id}
      metadata={scene.domain_metadata}
      onUpdate={(patch) => setDomainMetadata(patch)}
    />
  );
}

export { CapstoneDesignerComponent as CapstoneDesigner };

export const capstoneDesigner = new DesignerTool({
  id:         'platform.capstone_designer',
  label:      'Capstone designer',
  slot:       'RIGHT_INSP',
  category:   'metadata',
  applies_to: requiresSceneType('capstone'),
  Component:  CapstoneDesignerToolMount,
  weight:     60,
  helpText:   'Author brief, deliverables, rubric, and settings for capstone scenes.',
});

export default capstoneDesigner;
